<template>
  <div class="c-tdl-order-detail">
    <c-page-header>
      <template #extra>
        <div class="extra flex q-gap-sm">
          <template v-if="isShowOrderActions">
            <c-action label="取出包裹" @click="handleTakeout" />
            <c-action label="该单已送达" @click="handleDelivered" />
          </template>
        </div>
      </template>
    </c-page-header>

    <section class="c-tdl-order-detail__section-card">
      <div class="c-tdl-order-detail__section-header">
        <span class="c-tdl-order-detail__section-title">基础信息</span>
      </div>
      <div class="c-tdl-order-detail__section-body">
        <c-form-items
          :showOperation="false"
          :span="6"
          :items="[
            { label: '运单号', prop: 'thirdPartyOrderNo', type: 'text' },
            // { label: '物流公司', prop: 'logisticsCompany', type: 'text' },
            { label: '站点名称', prop: 'siteName', type: 'text' },
            { label: '无人车SN', prop: 'snCode', type: 'text' },
            { label: '无人车名称', prop: 'deviceName', type: 'text' },
            { label: '格口号', prop: 'gridCompartmentCode', type: 'text' },
            { label: '三方波次号', prop: 'thirdPartyWaveNo', type: 'text' },
            { label: '兔哒波次号', prop: 'waveNo', type: 'text', span: 12 }
          ]"
          :model="detailData"
        ></c-form-items>
      </div>
    </section>

    <section class="c-tdl-order-detail__section-card">
      <div class="c-tdl-order-detail__section-header">
        <span class="c-tdl-order-detail__section-title">配送信息</span>
      </div>
      <div class="c-tdl-order-detail__section-body">
        <c-form-items
          :showOperation="false"
          :span="6"
          :items="[
            { label: '小区名称', prop: 'communityName', type: 'text' },
            { label: '楼栋房号', prop: 'address', type: 'text' },
            { label: '收件人手机号', prop: 'contactPhone', type: 'text' },
            { invisible: true },
            { label: '起送时间', prop: 'deliveryStartTime', type: 'text' },
            { label: '送达时间', prop: 'actualDeliveryTime', type: 'text' },
            { label: '订单状态', prop: 'orderStatusName', type: 'text' },
            { label: '操作人手机号', prop: 'operatorPhone', type: 'text' }
          ]"
          :model="detailData"
        ></c-form-items>
      </div>
    </section>
    <section class="c-tdl-order-detail__section-card">
      <div class="c-tdl-order-detail__section-header">
        <span class="c-tdl-order-detail__section-title">订单物流轨迹</span>
      </div>

      <div v-if="logisticsTrackList?.length" class="c-tdl-order-detail__track">
        <c-table
          :columns="[
            { label: '时间', prop: 'pushTime', width: 150 },
            { label: '节点', prop: 'pushTypeName', width: 200 },
            { label: '备注', prop: 'pushMessage' }
          ]"
          :data="logisticsTrackList"
          border
          auto-height
          no-operation
          no-pager
          no-index
        />
      </div>
      <div v-else>暂无物流轨迹信息</div>
    </section>
    <section class="c-tdl-order-detail__section-card">
      <div class="c-tdl-order-detail__section-header">
        <span class="c-tdl-order-detail__section-title">订单操作日志</span>
      </div>

      <div v-if="operationLogs?.length" class="c-tdl-order-detail__log">
        <el-timeline>
          <el-timeline-item
            v-for="(log, index) in operationLogs"
            :key="index"
            :timestamp="log.gmtCreate"
            placement="top"
          >
            <div class="c-tdl-order-detail__log-main">{{ log.content }}</div>
            <div class="c-tdl-order-detail__log-operator">操作人：{{ log.operatorName }}</div>
          </el-timeline-item>
        </el-timeline>
      </div>
      <div v-else>当前设备暂无操作日志</div>
    </section>

    <section class="c-tdl-order-detail__section-card">
      <div class="c-tdl-order-detail__section-header">
        <span class="c-tdl-order-detail__section-title">照片凭证</span>
      </div>
      <div class="c-tdl-order-detail__section-body">
        <c-image-viewer v-if="photoList.length">
          <div class="photo-items flex">
            <div v-for="(it, index) in photoList" :key="`img_${index}`" class="photo-wrap">
              <c-image v-if="it.url" class="photo-img" :span="6" auto-height :src="it.url" />
              <div v-else class="no-photo">
                <div>暂无送达照片</div>
                <div>
                  <c-action label="点击刷新" buttonType="text" @click="reloadDeliveredPhoto()" />
                </div>
              </div>
              <div class="photo-label">{{ it.label }}</div>
            </div>
          </div>
        </c-image-viewer>
        <div v-else>暂无照片凭证</div>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { useCurrentAppInstance, computed, onMounted, ref, onActivated, reactive } from '@zto/zpage'
import { getCommonOptionLabel, getSpmFileUrls } from '@/utils'
import { commonOptions, TdlOptLogModule, TdlOrderStatus } from '@/config'

const app = useCurrentAppInstance()

const operationLogs = ref<any[]>([])
const logisticsTrackList = ref<any[]>([])
const detailData = ref<any>({})

const signImagePhotoUrl = ref<any>('')

const innerOrderId = computed(() => {
  return app.currentRoute.value.query.orderId
})

const isShowOrderActions = computed(() => {
  const status = detailData.value?.orderStatus
  return ![TdlOrderStatus.CANCEL, TdlOrderStatus.DELIVERED].includes(status)
})

/**
 * 照片列表，包含投递照片和送达照片
 */
const photoList = computed<any[]>(() => {
  const list: any[] = []

  const proofUrlStr = detailData.value?.orderFulfillmentProof?.proofValue

  if (proofUrlStr) {
    const proofUrls = proofUrlStr.split(',')
    proofUrls.forEach((url: string, index: number) => {
      list.push({ url, label: `投递照片`, type: 'send' })
    })
  }

  if (detailData.value?.orderStatus === TdlOrderStatus.DELIVERED) {
    list.push({ url: detailData.value.deliveryPhotoUrl || '', label: '送达照片', type: 'delivered' })
  }

  if (signImagePhotoUrl.value) {
    list.push({ url: signImagePhotoUrl.value, label: '签收照片', type: 'signed' })
  }

  return list
})

onMounted(() => {
  reloadData()
})

onActivated(() => {
  reloadData()
})

async function reloadData() {
  fetchDetailData()
  fetchOperationLogs()
  fetchLogisticTrackList()
}

/**
 * 重新加载送达照片，适用于标记已送达后刷新照片凭证
 */
async function reloadDeliveredPhoto() {
  if (!innerOrderId.value) return

  const res = await app.request({
    api: '/sys/admin/order/backfill-delivered-photo-by-wave',
    params: { orderId: innerOrderId.value }
  })

  if (!res.deliveryPhotoUrl) {
    app.ui.showMessage({ type: 'warning', message: '当前订单暂无送达照片' })
    return
  }

  app.ui.showMessage({ type: 'success', message: '成功获取送达照片' })

  // 刷新详情
  fetchDetailData()
}

async function fetchDetailData() {
  const res = await app.request({
    api: '/sys/admin/order/detail',
    params: {
      orderId: innerOrderId.value
    }
  })

  const orderInfo = res.orderInfo || {}
  const waveInfo = orderInfo.waveInfoDTO || {}

  if (orderInfo.signPhotoFileName) {
    try {
      const fileUrls = await getSpmFileUrls([orderInfo.signPhotoFileName])
      signImagePhotoUrl.value = fileUrls?.[0] || ''
    } catch (error) {
      console.error('获取签收照片失败', error)
    }
  }

  detailData.value = {
    ...orderInfo,
    waveNo: waveInfo.waveNo,
    thirdPartyWaveNo: waveInfo.thirdPartyTaskNo,
    orderStatusName: getCommonOptionLabel(commonOptions.tdlOrderStatus, orderInfo.orderStatus)
  }
}

async function fetchOperationLogs() {
  const res = await app.request({
    api: '/sys/admin/sys-config/queryOptLogList',
    params: {
      module: TdlOptLogModule.ORDER,
      bizId: innerOrderId.value,
      pageIndex: 1,
      pageSize: 10
    }
  })

  operationLogs.value = res.data || []
}

async function fetchLogisticTrackList() {
  const res = await app.request({
    api: '/sys/admin/logistics/push/trackByOrder',
    params: {
      orderId: innerOrderId.value
    }
  })

  logisticsTrackList.value = res?.trackList || []
}

async function handleTakeout() {
  await app.ui.showMessageBox({
    boxType: 'confirm',
    type: 'warning',
    showCancelButton: true,
    message: '确认要执行【取出包裹】操作吗？'
  })

  await app.request({
    api: '/sys/admin/order/cancelOrderList',
    params: {
      orderIdList: [innerOrderId.value]
    }
  })

  reloadData()

  emitOrderChangeEvent()
}

async function handleDelivered() {
  await app.ui.showMessageBox({
    boxType: 'confirm',
    type: 'warning',
    showCancelButton: true,
    message: '确认要标记该单【已送达】吗？'
  })

  await app.request({
    api: '/sys/admin/order/deliveredOrderList',
    params: {
      orderIdList: [innerOrderId.value]
    }
  })

  reloadData()

  emitOrderChangeEvent()
}

function emitOrderChangeEvent() {
  // 发送订单状态变更事件，通知订单列表刷新数据
  app.emits('$tdl_order_detail_status_change')
}
</script>

<style lang="scss" scoped>
.c-tdl-order-detail {
  &__title {
    flex: 1;
  }

  &__page-header {
    display: flex;
    .extra {
      display: flex;
      gap: 10px;
    }
  }

  &__section-header {
    padding: 10px 0;
  }

  &__section-title {
    font-size: 14px;
  }

  &__section-card {
    margin-top: 10px;
    padding: 8px 16px;
    background: #fff;
    border: 1px solid #edf1f7;
    border-radius: 8px;
    box-shadow: 0 8px 24px rgba(31, 42, 55, 0.05);

    ::v-deep(.form-item) {
      margin-bottom: 5px;
    }
  }

  &__log {
    &-main {
      padding: 2px 0;
    }

    &-operator {
      padding: 2px 0;
      color: #7b8794;
    }
  }
}

.photo-items {
  display: flex;
  gap: 10px;

  .photo-wrap {
    max-width: 200px;
    align-items: center;
    justify-content: center;

    ::v-deep(img) {
      max-width: 200px;
      height: 100px;
    }
  }

  .photo-label {
    text-align: center;
    padding: 5px;
  }

  .no-photo {
    display: flex;
    flex-direction: column;
    align-items: center;
    height: 100px;
    justify-content: center;
    color: #98a2b3;
    border-radius: 2px;
    padding: 0px 10px;
    background-color: #eee;
  }
}
</style>
